import { ButtonHTMLAttributes, forwardRef } from 'react';
import { VariantProps, cva } from 'class-variance-authority';
import Icon, { IconProps } from '@/components/ui/icon';
import Spinner, { SpinnerProps } from '@/components/ui/spinner';
import ButtonText, { ButtonTextProps } from '@/components/ui/typography/button-text';
import { cn } from '@/lib/utils';

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    children?: string;
    variant?: 'solid' | 'outlined' | 'borderless' | 'dashed';
    size?: 'xs' | 'sm' | 'md' | 'lg';
    fontWeight?: ButtonTextProps['weight'];
    color?:
      | 'achromatic'
      | 'achromatic-primary'
      | 'achromatic-info'
      | 'primary'
      | 'info'
      | 'success'
      | 'warning'
      | 'error';
    isLoading?: boolean;
    startIcon?: string;
    startIconFill?: boolean;
    endIcon?: string;
    endIconFill?: boolean;
  };

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'solid',
      size = 'md',
      color = 'primary',
      isLoading = false,
      startIcon,
      startIconFill,
      endIcon,
      endIconFill,
      className,
      type = 'button',
      children,
      disabled,
      fontWeight = 700,
      ...props
    },
    ref
  ) => (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      className={cn(buttonVariants({ variant, size, color }), className)}
      {...props}>
      {isLoading ? (
        <span className={`block absolute-center ${isLoading ? 'visible' : 'invisible'}`}>
          <Spinner
            color={getButtonSpinnerColor({ variant, color, disabled: disabled ?? false })}
            size={buttonSpinnerSizeMap[size]}
          />
        </span>
      ) : (
        <>
          {startIcon ? (
            <Icon
              name={startIcon}
              size={buttonIconAndButtonTextSizeMap[size]}
              fill={startIconFill}
              className="mr-1"
            />
          ) : null}
          <ButtonText size={buttonIconAndButtonTextSizeMap[size]} weight={fontWeight}>
            {children}
          </ButtonText>
          {endIcon ? (
            <Icon
              name={endIcon}
              size={buttonIconAndButtonTextSizeMap[size]}
              fill={endIconFill}
              className="ml-1"
            />
          ) : null}
        </>
      )}
    </button>
  )
);
Button.displayName = 'Button';

const buttonSpinnerSizeMap: Record<
  Exclude<ButtonProps['size'], undefined>,
  Exclude<SpinnerProps['size'], undefined>
> = {
  xs: '2xs',
  sm: 'xs',
  md: 'sm',
  lg: 'md',
};
const buttonIconAndButtonTextSizeMap: Record<
  Exclude<ButtonProps['size'], undefined>,
  | Exclude<IconProps['size'], 10 | 18 | 20 | 24 | 30 | undefined>
  | Exclude<ButtonTextProps['size'], undefined>
> = {
  xs: 12,
  sm: 14,
  md: 14,
  lg: 16,
};

const getButtonSpinnerColor = (options: {
  variant: ButtonProps['variant'];
  disabled: boolean;
  color: SpinnerProps['color'];
}) => {
  switch (true) {
    case options.disabled:
      return 'achromatic';
    case !options.disabled && options.variant !== 'solid':
      return options.color;
    default:
      return undefined;
  }
};

const buttonVariants = cva(
  [
    'flex',
    'items-center',
    'justify-center',
    'rounded-5',
    'relative',
    'transition-colors',
    'disabled:cursor-not-allowed',
  ],
  {
    variants: {
      variant: {
        solid: ['text-white', 'disabled:bg-achromatic-disabled-dark'],
        outlined: [
          'border',
          'border-solid',
          'bg-white',
          'disabled:text-achromatic-disabled-dark',
          'disabled:border-achromatic-disabled-dark',
        ],
        borderless: ['bg-transparent', 'disabled:text-achromatic-disabled-dark'],
        dashed: [
          'border',
          'border-dashed',
          'bg-white',
          'disabled:text-achromatic-disabled-dark',
          'disabled:border-achromatic-disabled-dark',
        ],
      },
      color: {
        achromatic: '',
        'achromatic-primary': '',
        'achromatic-info': '',
        primary: '',
        info: '',
        warning: '',
        success: '',
        error: '',
      },
      size: {
        xs: ['min-h-[1.625rem]', 'px-2.5'],
        sm: ['min-h-[1.875rem]', 'px-2.5'],
        md: ['min-h-9', 'px-4'],
        lg: ['min-h-11', 'px-4'],
      },
    },
    compoundVariants: [
      {
        variant: 'solid',
        color: 'achromatic',
        className: [
          'enabled:bg-achromatic-light',
          'enabled:hover:bg-achromatic-60',
          'enabled:active:bg-achromatic-80',
          'enabled:data-[state=active]:bg-achromatic-80',
        ],
      },
      {
        variant: 'solid',
        color: 'achromatic-primary',
        className: [
          'enabled:bg-achromatic-light',
          'enabled:hover:bg-primary-light',
          'enabled:active:bg-primary-dark',
          'enabled:data-[state=active]:bg-primary-dark',
        ],
      },
      {
        variant: 'solid',
        color: 'achromatic-info',
        className: [
          'enabled:bg-achromatic-light',
          'enabled:hover:bg-info-light',
          'enabled:active:bg-info-dark',
          'enabled:data-[state=active]:bg-info-dark',
        ],
      },
      {
        variant: 'solid',
        color: 'primary',
        className: [
          'enabled:bg-primary',
          'enabled:hover:bg-primary-light',
          'enabled:active:bg-primary-dark',
          'enabled:data-[state=active]:bg-primary-dark',
        ],
      },
      {
        variant: 'solid',
        color: 'info',
        className: [
          'enabled:bg-info',
          'enabled:hover:bg-info-light',
          'enabled:active:bg-info-dark',
          'enabled:data-[state=active]:bg-info-dark',
        ],
      },
      {
        variant: 'solid',
        color: 'success',
        className: [
          'enabled:bg-success',
          'enabled:hover:bg-success-light',
          'enabled:active:bg-success-dark',
          'enabled:data-[state=active]:bg-success-dark',
        ],
      },
      {
        variant: 'solid',
        color: 'warning',
        className: [
          'enabled:bg-warning',
          'enabled:hover:bg-warning-light',
          'enabled:active:bg-warning-dark',
          'enabled:data-[state=active]:bg-warning-dark',
        ],
      },
      {
        variant: 'solid',
        color: 'error',
        className: [
          'enabled:bg-error',
          'enabled:hover:bg-error-light',
          'active:bg-error-dark',
          'data-[state=active]:bg-error-dark',
        ],
      },
      {
        variant: 'outlined',
        color: 'achromatic',
        className: [
          'enabled:text-achromatic-light',
          'enabled:border-achromatic-border',
          'enabled:hover:bg-achromatic-hover',
          'enabled:active:bg-achromatic-active',
          'enabled:data-[state=active]:bg-achromatic-active',
        ],
      },
      {
        variant: 'outlined',
        color: 'achromatic-primary',
        className: [
          'enabled:text-achromatic-light',
          'enabled:border-achromatic-border',
          'enabled:hover:text-primary-light',
          'enabled:hover:border-primary-light',
          'enabled:hover:bg-primary-hover',
          'enabled:active:text-primary-dark',
          'enabled:active:border-primary-dark',
          'enabled:active:bg-primary-active',
          'enabled:data-[state=active]:text-primary-dark',
          'enabled:data-[state=active]:border-primary-dark',
          'enabled:data-[state=active]:bg-primary-active',
        ],
      },
      {
        variant: 'outlined',
        color: 'achromatic-info',
        className: [
          'enabled:text-achromatic-light',
          'enabled:border-achromatic-border',
          'enabled:hover:text-info-light',
          'enabled:hover:border-info-light',
          'enabled:hover:bg-info-hover',
          'enabled:active:text-info-dark',
          'enabled:active:border-info-dark',
          'enabled:active:bg-info-active',
          'enabled:data-[state=active]:text-info-dark',
          'enabled:data-[state=active]:border-info-dark',
          'enabled:data-[state=active]:bg-info-active',
        ],
      },
      {
        variant: 'outlined',
        color: 'primary',
        className: [
          'enabled:text-primary',
          'enabled:border-primary',
          'enabled:hover:text-primary-light',
          'enabled:hover:border-primary-light',
          'enabled:hover:bg-primary-hover',
          'enabled:active:text-primary-dark',
          'enabled:active:border-primary-dark',
          'enabled:active:bg-primary-active',
          'enabled:data-[state=active]:text-primary-dark',
          'enabled:data-[state=active]:border-primary-dark',
          'enabled:data-[state=active]:bg-primary-active',
        ],
      },
      {
        variant: 'outlined',
        color: 'info',
        className: [
          'enabled:text-info',
          'enabled:border-info',
          'enabled:hover:text-info-light',
          'enabled:hover:border-info-light',
          'enabled:hover:bg-info-hover',
          'enabled:active:text-info-dark',
          'enabled:active:border-info-dark',
          'enabled:active:bg-info-active',
          'enabled:data-[state=active]:text-info-dark',
          'enabled:data-[state=active]:border-info-dark',
          'enabled:data-[state=active]:bg-info-active',
        ],
      },
      {
        variant: 'outlined',
        color: 'warning',
        className: [
          'enabled:text-warning',
          'enabled:border-warning',
          'enabled:hover:text-warning-light',
          'enabled:hover:border-warning-light',
          'enabled:hover:bg-warning-hover',
          'enabled:active:text-warning-dark',
          'enabled:active:border-warning-dark',
          'enabled:active:bg-warning-active',
          'enabled:data-[state=active]:text-warning-dark',
          'enabled:data-[state=active]:border-warning-dark',
          'enabled:data-[state=active]:bg-warning-active',
        ],
      },
      {
        variant: 'outlined',
        color: 'success',
        className: [
          'enabled:text-success',
          'enabled:border-success',
          'enabled:hover:text-success-light',
          'enabled:hover:border-success-light',
          'enabled:hover:bg-success-hover',
          'enabled:active:text-success-dark',
          'enabled:active:border-success-dark',
          'enabled:active:bg-success-active',
          'enabled:data-[state=active]:text-success-dark',
          'enabled:data-[state=active]:border-success-dark',
          'enabled:data-[state=active]:bg-success-active',
        ],
      },
      {
        variant: 'outlined',
        color: 'error',
        className: [
          'enabled:text-error',
          'enabled:border-error',
          'enabled:hover:text-error-light',
          'enabled:hover:border-error-light',
          'enabled:hover:bg-error-hover',
          'enabled:active:text-error-dark',
          'enabled:active:border-error-dark',
          'enabled:active:bg-error-active',
          'enabled:data-[state=active]:text-error-dark',
          'enabled:data-[state=active]:border-error-dark',
          'enabled:data-[state=active]:bg-error-active',
        ],
      },
      {
        variant: 'borderless',
        color: 'achromatic',
        className: [
          'enabled:text-achromatic-light',
          'enabled:hover:bg-achromatic-hover',
          'enabled:active:bg-achromatic-active',
          'enabled:data-[state=active]:bg-achromatic-active',
        ],
      },
      {
        variant: 'borderless',
        color: 'achromatic-primary',
        className: [
          'enabled:text-achromatic-light',
          'enabled:hover:text-primary-light',
          'enabled:hover:bg-primary-hover',
          'enabled:active:text-primary-dark',
          'enabled:active:bg-primary-active',
          'enabled:data-[state=active]:text-primary-dark',
          'enabled:data-[state=active]:bg-primary-active',
        ],
      },
      {
        variant: 'borderless',
        color: 'achromatic-info',
        className: [
          'enabled:text-achromatic-light',
          'enabled:hover:text-info-light',
          'enabled:hover:bg-info-hover',
          'enabled:active:text-info-dark',
          'enabled:active:bg-info-active',
          'enabled:data-[state=active]:text-info-dark',
          'enabled:data-[state=active]:bg-info-active',
        ],
      },
      {
        variant: 'borderless',
        color: 'primary',
        className: [
          'enabled:text-primary',
          'enabled:hover:text-primary-light',
          'enabled:hover:bg-primary-hover',
          'enabled:active:text-primary-dark',
          'enabled:active:bg-primary-active',
          'enabled:data-[state=active]:text-primary-dark',
          'enabled:data-[state=active]:bg-primary-active',
        ],
      },
      {
        variant: 'borderless',
        color: 'info',
        className: [
          'enabled:text-info',
          'enabled:hover:text-info-light',
          'enabled:hover:bg-info-hover',
          'enabled:active:text-info-dark',
          'enabled:active:bg-info-active',
          'enabled:data-[state=active]:text-info-dark',
          'enabled:data-[state=active]:bg-info-active',
        ],
      },
      {
        variant: 'borderless',
        color: 'warning',
        className: [
          'text-warning',
          'hover:text-warning-light',
          'hover:bg-warning-hover',
          'active:text-warning-dark',
          'active:bg-warning-active',
          'data-[state=active]:text-warning-dark',
          'data-[state=active]:bg-warning-active',
        ],
      },
      {
        variant: 'borderless',
        color: 'success',
        className: [
          'enabled:text-success',
          'enabled:hover:text-success-light',
          'enabled:hover:bg-success-hover',
          'enabled:active:text-success-dark',
          'enabled:active:bg-success-active',
          'enabled:data-[state=active]:text-success-dark',
          'enabled:data-[state=active]:bg-success-active',
        ],
      },
      {
        variant: 'borderless',
        color: 'error',
        className: [
          'enabled:text-error',
          'enabled:hover:text-error-light',
          'enabled:hover:bg-error-hover',
          'enabled:active:text-error-dark',
          'enabled:active:bg-error-active',
          'enabled:data-[state=active]:text-error-dark',
          'enabled:data-[state=active]:bg-error-active',
        ],
      },
      {
        variant: 'dashed',
        color: 'achromatic',
        className: [
          'enabled:text-achromatic-light',
          'enabled:border-achromatic-border',
          'enabled:hover:bg-achromatic-hover',
          'enabled:active:bg-achromatic-active',
          'enabled:data-[state=active]:bg-achromatic-active',
        ],
      },
      {
        variant: 'dashed',
        color: 'achromatic-primary',
        className: [
          'enabled:text-achromatic-light',
          'enabled:border-achromatic-border',
          'enabled:hover:text-primary-light',
          'enabled:hover:border-primary-light',
          'enabled:hover:bg-primary-hover',
          'enabled:active:text-primary-dark',
          'enabled:active:border-primary-dark',
          'enabled:active:bg-primary-active',
          'enabled:data-[state=active]:text-primary-dark',
          'enabled:data-[state=active]:border-primary-dark',
          'enabled:data-[state=active]:bg-primary-active',
        ],
      },
      {
        variant: 'dashed',
        color: 'achromatic-info',
        className: [
          'enabled:text-achromatic-light',
          'enabled:border-achromatic-border',
          'enabled:hover:text-info-light',
          'enabled:hover:border-info-light',
          'enabled:hover:bg-info-hover',
          'enabled:active:text-info-dark',
          'enabled:active:border-info-dark',
          'enabled:active:bg-info-active',
          'enabled:data-[state=active]:text-info-dark',
          'enabled:data-[state=active]:border-info-dark',
          'enabled:data-[state=active]:bg-info-active',
        ],
      },
      {
        variant: 'dashed',
        color: 'primary',
        className: [
          'enabled:text-primary',
          'enabled:border-primary',
          'enabled:hover:text-primary-light',
          'enabled:hover:border-primary-light',
          'enabled:hover:bg-primary-hover',
          'enabled:active:text-primary-dark',
          'enabled:active:border-primary-dark',
          'enabled:active:bg-primary-active',
          'enabled:data-[state=active]:text-primary-dark',
          'enabled:data-[state=active]:border-primary-dark',
          'enabled:data-[state=active]:bg-primary-active',
        ],
      },
      {
        variant: 'dashed',
        color: 'info',
        className: [
          'enabled:text-info',
          'enabled:border-info',
          'enabled:hover:text-info-light',
          'enabled:hover:border-info-light',
          'enabled:hover:bg-info-hover',
          'enabled:active:text-info-dark',
          'enabled:active:border-info-dark',
          'enabled:active:bg-info-active',
          'enabled:data-[state=active]:text-info-dark',
          'enabled:data-[state=active]:border-info-dark',
          'enabled:data-[state=active]:bg-info-active',
        ],
      },
      {
        variant: 'dashed',
        color: 'warning',
        className: [
          'enabled:text-warning',
          'enabled:border-warning',
          'enabled:hover:text-warning-light',
          'enabled:hover:border-warning-light',
          'enabled:hover:bg-warning-hover',
          'enabled:active:text-warning-dark',
          'enabled:active:border-warning-dark',
          'enabled:active:bg-warning-active',
          'enabled:data-[state=active]:text-warning-dark',
          'enabled:data-[state=active]:border-warning-dark',
          'enabled:data-[state=active]:bg-warning-active',
        ],
      },
      {
        variant: 'dashed',
        color: 'success',
        className: [
          'enabled:text-success',
          'enabled:border-success',
          'enabled:hover:text-success-light',
          'enabled:hover:border-success-light',
          'enabled:hover:bg-success-hover',
          'enabled:active:text-success-dark',
          'enabled:active:border-success-dark',
          'enabled:active:bg-success-active',
          'enabled:data-[state=active]:text-success-dark',
          'enabled:data-[state=active]:border-success-dark',
          'enabled:data-[state=active]:bg-success-active',
        ],
      },
      {
        variant: 'dashed',
        color: 'error',
        className: [
          'enabled:text-error',
          'enabled:border-error',
          'enabled:hover:text-error-light',
          'enabled:hover:border-error-light',
          'enabled:hover:bg-error-hover',
          'enabled:active:text-error-dark',
          'enabled:active:border-error-dark',
          'enabled:active:bg-error-active',
          'enabled:data-[state=active]:text-error-dark',
          'enabled:data-[state=active]:border-error-dark',
          'enabled:data-[state=active]:bg-error-active',
        ],
      },
    ],
  }
);

export default Button;
